// angular
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Params } from '@angular/router';
// other libraries
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateService } from '@ngx-translate/core';
// components
import { MessageBoxComponent } from '../message-box/message-box.component';
import { SuggestMessageBoxComponent } from '../suggest-message-box/suggest-message-box.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { PaginationOnScrollComponent } from '../pagination-on-scroll/pagination-on-scroll.component';
import { MessagesSkeletonComponent } from '../messages-skeleton/messages-skeleton.component';
import { PublicChatFormComponent } from '../public-chat-form/public-chat-form.component';
// services
import { UserService } from '@app/shared/services/user.service';
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { ChatService } from '@app/+chat/shared/services/chat.service';
import { MessagingService } from '@app/+chat/shared/services/messaging.service';
import { StorageService } from '@app/shared/services/storage.service';
// interfaces & envs
import { IMessage } from '@app/+chat/shared/interfaces';
import { environment } from '@environments/environment';
// enums
import { AlertStatesEnum } from '@app/+chat/shared/enums/alert-states.enum';

@Component({
  selector: 'pwj-chat-messages-container',
  standalone: true,
  imports: [
    MessageBoxComponent,
    SuggestMessageBoxComponent,
    NgxSkeletonLoaderModule,
    ChatInputComponent,
    PaginationOnScrollComponent,
    MessagesSkeletonComponent,
    PublicChatFormComponent,
  ],
  templateUrl: './chat-messages-container.component.html',
  styleUrl: './chat-messages-container.component.scss',
})
export class ChatMessagesContainerComponent implements OnInit, AfterViewInit {
  public alertState!: AlertStatesEnum;
  public messages: IMessage[] = [];
  public messagesCount!: number;
  public suggestionMessages: string[] = [];
  public isMessagesLoading!: boolean;
  public showPagination: boolean = true;
  public lastPageHeight: number = 0;
  public isChatLoading!: boolean;

  private _token!: string;
  private _chatId!: string;

  @ViewChild('messagesContainer', { static: false })
  public messagesContainer!: ElementRef;

  @ViewChildren('message') public messageElement!: QueryList<ElementRef>;

  public readonly stateManagement = inject(StateManagementService);
  private readonly _userService = inject(UserService);
  private readonly _chatService = inject(ChatService);
  private readonly _messagingService = inject(MessagingService);
  private readonly _translateService = inject(TranslateService);
  private readonly _storageService = inject(StorageService);

  constructor() {
    this._userService.getToken().then(res => {
      if (res !== null) {
        this._token = res as string;
        this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
      }
      this.setPublicChatForm();
    });

    effect(() => {
      this.alertState = this.stateManagement.chatAlertState();
    });

    this._translateService.onLangChange.subscribe(_ => {
      this.setSuggestionsMessages();
    });

    effect(() => {
      this.isChatLoading = this.stateManagement.isChatLoading();
    });

    effect(() => {
      this.isMessagesLoading = this.stateManagement.isMessagesLoading();
    });

    effect(() => {
      const chat = this.stateManagement.oneChatById();

      if (chat?._id) {
        this._chatId = chat._id;
        this.scrollToBottom();
        this.getAllMessages(this._chatId);
      }
    });

    effect(() => {
      if (this.stateManagement?.allMessagesCount())
        this.messagesCount = this.stateManagement.allMessagesCount() as number;
    });

    effect(() => {
      this.messages = this.stateManagement.allMessages();
      setTimeout(() => {
        this.showPagination = true;
      }, environment.chatPaginationTimeOut);
    });
  }

  ngOnInit(): void {
    this.setSuggestionsMessages();
  }

  public startChatWithSupport(chatId?: string) {
    if (chatId) {
      this._chatService.getChatByChatId(chatId);
      this.scrollToBottom();
      this.getAllMessages(chatId);
    } else {
      this._chatService.createSupportChat();
    }

    this.stateManagement.isMessagesLoading.set(true);
  }

  public fetchDataForNextPage() {
    this.showPagination = false;
    this.lastPageHeight = this.messagesContainer?.nativeElement?.scrollHeight;

    const currentPage = this.stateManagement.messagesCurrentPage();
    const allPagesCount = this.stateManagement.messagesAllPages();

    if (currentPage < allPagesCount) {
      const params = {
        page: currentPage + 1 || 0,
        limit: 20,
      };
      this.getAllMessages(this._chatId, params);
    }
  }

  public getAllMessages(id: string, params?: Params) {
    this._messagingService.getAllMessages(id, params);
  }

  ngAfterViewInit() {
    this.messageElement?.changes.subscribe(_ => this.scrollToBottom());
  }

  public scrollToBottom() {
    this.messagesContainer?.nativeElement.scrollTo({
      top:
        this.messages?.length > 0 && !this.showPagination
          ? this.lastPageHeight || 10
          : this.messagesContainer.nativeElement.scrollHeight,
      left: 0,
      behavior: 'smooth',
    });
  }

  public closeChatBox() {
    this.stateManagement.isChatBoxOpen.set(false);
  }

  public setSuggestionsMessages() {
    this.suggestionMessages = [
      this._translateService.instant('chat.suggestionMessages.item1'),
      this._translateService.instant('chat.suggestionMessages.item2'),
      this._translateService.instant('chat.suggestionMessages.item3'),
      this._translateService.instant('chat.suggestionMessages.item4'),
      this._translateService.instant('chat.suggestionMessages.item5'),
    ];
  }

  public setPublicChatForm() {
    const chatId = this._storageService.getChatId();

    if (this._token && !chatId) {
      this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
      this.startChatWithSupport();
    } else if (chatId) {
      this.startChatWithSupport(chatId);
      this.stateManagement.chatAlertState.set(AlertStatesEnum.supportChat);
    } else {
      this.stateManagement.chatAlertState.set(AlertStatesEnum.publicChat);
    }
  }

  ngOnDestroy(): void {
    this.stateManagement.selectedSuggestionMessage.set('');
  }

  protected readonly AlertStatesEnum = AlertStatesEnum;
}
